/**
 * InvoiceAmountWithEvents — light-touch popover for invoice tables.
 *
 * Renders the invoice amount with a subtle dotted underline. On hover
 * (or focus), fires GET /api/invoices/{id}/money-events once and shows
 * a compact dark popover listing every $ event tied to this invoice
 * — payments, fees, refunds, factoring, credit memos.
 *
 * The Activity tab cross-links INTO the invoice list via the chip
 * pattern in WalletActivityPanel; this component is the inverse —
 * cross-links FROM each invoice OUT to its $ events without leaving
 * the page. Together they give the seller (and buyer) two friction-
 * free pivots between ledger and register views.
 *
 * Caller:
 *   <InvoiceAmountWithEvents invoiceId={i.id} amount={i.amount}
 *                            subText={ … optional secondary lines … }/>
 */
import { useEffect, useRef, useState } from "react";
import { Loader2, Receipt } from "lucide-react";
import api, { money } from "../lib/api";

const KIND_TONE = {
  "Paid":          { dot: "bg-emerald-400", num: "text-emerald-300" },
  "Refund":        { dot: "bg-amber-400",   num: "text-amber-300" },
  "Fee":           { dot: "bg-foreground/30", num: "opacity-65" },
  "Factored":      { dot: "bg-sky-400",     num: "text-sky-300" },
  "Repaid":        { dot: "bg-foreground/30", num: "opacity-65" },
  "Credit memo":   { dot: "bg-amber-400",   num: "text-amber-300" },
  "Top-up":        { dot: "bg-emerald-400", num: "text-emerald-300" },
  "Withdrawal":    { dot: "bg-foreground/30", num: "opacity-65" },
};

export default function InvoiceAmountWithEvents({ invoiceId, amount, subText = null }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState(null);
  const [err, setErr]       = useState(null);
  const fetchedRef = useRef(false);
  const hoverTimeoutRef = useRef(null);

  // Lazy-load on first hover/focus, cache on the ref so the popover
  // doesn't re-fetch when the user toggles in and out.
  const loadEvents = async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    try {
      const { data } = await api.get(`/invoices/${invoiceId}/money-events`);
      setEvents(data?.events || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load events");
      setEvents([]);
    }
  };

  // 200ms hover-intent delay so a casual mouse-over doesn't fire a
  // request the user didn't actually want.
  const onEnter = () => {
    clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setOpen(true);
      loadEvents();
    }, 200);
  };
  const onLeave = () => {
    clearTimeout(hoverTimeoutRef.current);
    setOpen(false);
  };

  useEffect(() => () => clearTimeout(hoverTimeoutRef.current), []);

  return (
    <div className="relative inline-block"
         onMouseEnter={onEnter}
         onMouseLeave={onLeave}
         onFocus={onEnter}
         onBlur={onLeave}
         tabIndex={0}
         data-testid={`inv-amount-${invoiceId}`}>
      <div className="display border-b border-dotted border-foreground/30 cursor-help inline-block">
        {money(amount)}
      </div>
      {subText}
      {open && (
        <div
          className="absolute z-50 left-0 top-full mt-1.5 w-80 max-w-[90vw] bg-foreground text-background rounded-lg shadow-xl p-3 text-[12px]"
          role="tooltip"
          data-testid={`inv-amount-popover-${invoiceId}`}
        >
          <div className="flex items-center gap-1.5 mb-2 text-[10.5px] mono uppercase tracking-[0.18em] opacity-65">
            <Receipt className="w-3 h-3"/>
            Money events
          </div>

          {events === null && !err && (
            <div className="py-3 text-center opacity-75 inline-flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin"/>Loading…
            </div>
          )}

          {err && (
            <div className="py-2 text-amber-300 text-[11.5px]">{err}</div>
          )}

          {events && events.length === 0 && (
            <div className="py-2 opacity-65 text-[11.5px]">
              No money events on this invoice yet — once it's paid, factored, or
              refunded, the breakdown shows up here.
            </div>
          )}

          {events && events.length > 0 && (
            <ul className="space-y-1.5">
              {events.map((e, idx) => {
                const tone = KIND_TONE[e.kind] || KIND_TONE.Fee;
                return (
                  <li key={e.id || idx} className="flex items-start gap-2"
                      data-testid={`inv-event-${e.id || idx}`}>
                    <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${tone.dot}`}/>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="truncate">
                          <span className="font-medium opacity-95">{e.kind}</span>
                          {e.note && <span className="opacity-65"> · {e.note}</span>}
                        </div>
                        <div className={`tabular-nums shrink-0 ${tone.num}`}>
                          {e.amount > 0 ? "+" : e.amount < 0 ? "" : ""}
                          {e.amount !== 0 ? "$" + Math.abs(e.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
                        </div>
                      </div>
                      {e.when && (
                        <div className="opacity-55 text-[10.5px] mono mt-0.5">
                          {new Date(e.when).toLocaleString(undefined, {
                            month: "short", day: "numeric", year: "numeric",
                            hour: "numeric", minute: "2-digit",
                          })}
                          {e.ref && <span> · {e.ref}</span>}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
